import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlagIcon from '@mui/icons-material/OutlinedFlag';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChallengeHero from '../components/domain/ChallengeHero';
import api from '../api/client';

const ICON_MAP = {
  0: <DirectionsRunIcon />,
  1: <PhotoCameraIcon />,
  2: <QrCodeIcon />,
};

const STATUS_CHIP = {
  active: { label: 'Aktywna', color: 'success' },
  scheduled: { label: 'Zaplanowana', color: 'info' },
  registration_open: { label: 'W trakcie zapisów', color: 'info' },
  wind_down: { label: 'Rozliczenie', color: 'warning' },
  draft: { label: 'Draft', color: 'default' },
  paused: { label: 'Wstrzymana', color: 'warning' },
  completed: { label: 'Zakończona', color: 'default' },
  cancelled: { label: 'Odwołana', color: 'error' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
}

function parseJsonField(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ activeCampaigns: 0, totalParticipants: 0, engagement: '—' });

  useEffect(() => {
    Promise.all([
      api.get('/api/campaigns').catch(() => []),
      api.get('/api/tenants/me').catch(() => null),
      api.get('/api/tenants/me/users').catch(() => []),
    ]).then(([campaignData, tenantData, usersData]) => {
      const list = Array.isArray(campaignData) ? campaignData : campaignData?.campaigns || [];
      setCampaigns(list);
      const userList = Array.isArray(usersData) ? usersData : usersData?.users || [];
      setUsers(userList);
      const active = list.filter((c) => c.status === 'active').length;
      setStats({
        activeCampaigns: active,
        totalParticipants: userList.length || tenantData?.total_users || 0,
        engagement: '68%',
      });
      setLoading(false);
    });
  }, []);

  const METRICS = [
    {
      label: 'Aktywne kampanie',
      value: stats.activeCampaigns,
      icon: <FlagIcon fontSize="small" />,
    },
    {
      label: 'Uczestnicy',
      value: stats.totalParticipants,
      icon: <GroupOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Średnie zaangażowanie',
      value: stats.engagement,
      icon: <TrendingUpIcon fontSize="small" />,
    },
  ];

  const heroCampaign = campaigns.find((c) => c.status === 'active') || campaigns[0];
  const otherCampaigns = campaigns.filter((c) => c !== heroCampaign);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontFamily: theme.typography.h1.fontFamily,
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            Kampanie
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Zarządzaj wyzwaniami, konkursami i wydarzeniami
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          component={Link}
          to="/campaigns/templates"
          fullWidth={false}
          sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
        >
          Utwórz kampanię
        </Button>
      </Box>

      {/* Metric Strip — sekcja 8.3, bez osobnych kart cieniowanych */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        {METRICS.map((metric) => (
          <Box
            key={metric.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1.5,
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '0 1 auto' },
              borderRadius: `${theme.shape.borderRadius}px`,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <Avatar
              sx={{
                bgcolor: theme.tokens?.semantic.surface.brandTint,
                color: 'primary.main',
                width: 32,
                height: 32,
              }}
            >
              {metric.icon}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}
              >
                {metric.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metric.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Hero Challenge Card — sekcja 8.1, jedyny element poziomu Hero na tym ekranie */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {heroCampaign && (
            <Box sx={{ mb: 4 }}>
              <ChallengeHero
                title={heroCampaign.title || 'Razem do 10 milionów kroków'}
                statusLabel={STATUS_CHIP[heroCampaign.status]?.label || 'Aktywna'}
                metricValue={(() => {
                  const charity = parseJsonField(heroCampaign.charity, {});
                  return charity.currentAmount
                    ? charity.currentAmount.toLocaleString('pl-PL')
                    : '0';
                })()}
                metricLabel="zdobytych"
                progressValue={(() => {
                  const charity = parseJsonField(heroCampaign.charity, {});
                  return charity.currentAmount && charity.goalAmount
                    ? Math.round((charity.currentAmount / charity.goalAmount) * 100)
                    : 0;
                })()}
                participantsValue={users.length || heroCampaign.participants || 0}
                participantsLabel="uczestników"
                ctaLabel="Zobacz wyniki"
                onCtaClick={() => navigate(`/campaigns/${heroCampaign.id}`)}
              />
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ fontFamily: theme.typography.h1.fontFamily, mb: 2 }}>
                Pozostałe kampanie
              </Typography>
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {otherCampaigns.map((campaign, idx) => {
                  const status = STATUS_CHIP[campaign.status] || STATUS_CHIP.draft;
                  return (
                    <ListItem key={campaign.id} disablePadding>
                      <ListItemButton
                        onClick={() => navigate(`/campaigns/${campaign.id}`)}
                        sx={{
                          borderRadius: `${theme.tokens?.primitive.radius.card ?? 20}px`,
                          border: `1px solid ${theme.palette.divider}`,
                          p: 2,
                          flexWrap: 'wrap',
                          rowGap: 1,
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: theme.tokens?.semantic.surface.brandTint,
                            color: 'primary.main',
                            borderRadius: `${theme.tokens?.primitive.radius.control ?? 12}px`,
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          {ICON_MAP[idx % 3] || <DirectionsRunIcon />}
                        </Avatar>
                        <ListItemText
                          primary={campaign.title}
                          secondary={`${formatDate(campaign.start_date)} – ${formatDate(campaign.end_date)}`}
                          primaryTypographyProps={{ fontWeight: 650 }}
                          sx={{ minWidth: 0, flexBasis: { xs: '60%', sm: 'auto' } }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            ml: { xs: 'auto', sm: 0 },
                            flexShrink: 0,
                          }}
                        >
                          <Chip label={status.label} color={status.color} size="small" />
                          <Box
                            sx={{
                              display: { xs: 'none', sm: 'flex' },
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'text.secondary',
                            }}
                          >
                            <GroupOutlinedIcon fontSize="small" />
                            <Typography variant="body2">{campaign.participants}</Typography>
                          </Box>
                          <ChevronRightIcon
                            color="disabled"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default Dashboard;
